import { XMLParser } from 'fast-xml-parser';
import { useMemo, useRef, type ReactNode } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { QuickAction } from '~/components/chat/QuickAction';
import { workbenchStore } from '~/lib/stores/workbench';
import { createScopedLogger } from '~/utils/logger';
import {
  BOLT_QUICK_ACTIONS_REGEX,
  SUPPORTED_QUICK_ACTIONS,
  type QuickActionList,
  type QuickActionUnion,
} from '~/utils/quick-actions';
import { addWorkDir } from '~/utils/workDir';

const logger = createScopedLogger('useQuickActions');

function isValidQuickAction(action: QuickActionUnion): boolean {
  const files = workbenchStore.files.get();

  const isValidMessageAction = (action.type === 'implement' || action.type === 'message') && action.message?.trim();
  const isValidFileAction = action.type === 'file' && files[addWorkDir(action.path)]?.type === 'file';
  const isValidLinkAction = action.type === 'link' && action.href;

  return !!(
    SUPPORTED_QUICK_ACTIONS.has(action.type) &&
    action.text?.trim() &&
    (isValidMessageAction || isValidFileAction || isValidLinkAction)
  );
}

export function useQuickActions(content: string, disableQuickActions?: boolean, scrollToBottom?: ScrollToBottom) {
  const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: '', textNodeName: 'text' });

  const previousQuickActionsRef = useRef<{ match: string; disableQuickActions?: boolean } | undefined>();
  const cacheRef = useRef<{ markdownContent: string; quickActions: ReactNode[] }>();

  return useMemo(() => {
    let markdownContent = content;
    let quickActions: ReactNode[] = [];

    const match = content.match(BOLT_QUICK_ACTIONS_REGEX);
    const currentMatch = match?.[1];

    if (!currentMatch) {
      return { markdownContent, quickActions };
    }

    if (
      cacheRef.current &&
      currentMatch === previousQuickActionsRef.current?.match &&
      previousQuickActionsRef.current?.disableQuickActions === disableQuickActions
    ) {
      return cacheRef.current;
    }

    previousQuickActionsRef.current = { match: currentMatch, disableQuickActions };

    const result = parser.parse(match[1]) as Record<string, QuickActionUnion | QuickActionList | undefined | null>;

    logger.debug('Parse markdown content');

    const parsedQuickActions = result['bolt-quick-action'];

    const boltQuickActions = !parsedQuickActions
      ? []
      : Array.isArray(parsedQuickActions)
        ? parsedQuickActions
        : [parsedQuickActions];

    quickActions = boltQuickActions.flatMap((action, index) => {
      if (!isValidQuickAction(action)) {
        return [];
      }

      return (
        <QuickAction
          key={`${action.type}:${index}`}
          action={action}
          disabled={disableQuickActions}
          scrollToBottom={scrollToBottom}
        />
      );
    });

    markdownContent = content.replace(BOLT_QUICK_ACTIONS_REGEX, '').trimEnd();

    cacheRef.current = { markdownContent, quickActions };

    return cacheRef.current;
  }, [content, disableQuickActions, scrollToBottom]);
}
